import { useAuthStore } from '@/stores/auth'
import { DashboardFilters } from '@/services/dashboard-filters.ts'

const decodeToken = (token: string) => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
  }).join(''))
  return JSON.parse(jsonPayload)
}


export class AuthService {
  private static readonly REDIRECT_KEY = 'revio.redirect_url'

  static setRedirectUrl(url: string): void {
    sessionStorage.setItem(this.REDIRECT_KEY, url)
  }

  static consumeRedirectUrl(): string {
    const url = sessionStorage.getItem(this.REDIRECT_KEY)
    sessionStorage.removeItem(this.REDIRECT_KEY)

    // Only validate after login — must be a relative path and not an auth route
    if (
      url &&
      url.startsWith('/') &&
      !url.startsWith('//') &&
      !url.startsWith('/login') &&
      !url.startsWith('/sso/') &&
      !url.startsWith('/reset_password')
    ) {
      return url
    }

    return '/dashboard'
  }

  static setTokens(accessToken: string, refreshToken: string, isUpdate: boolean = false): void {
    const authStore = useAuthStore()
    const expiresAt = decodeToken(accessToken).exp * 1000
    authStore.setTokens(accessToken, refreshToken, expiresAt)

    if (!isUpdate) {
      DashboardFilters.reset()
    }
  }

  static isAccessValid(): boolean {
    const authStore = useAuthStore()
    return !!authStore.accessToken && !!authStore.expiresAt && authStore.expiresAt > Date.now()
  }

  static isRefreshValid(): boolean {
    const authStore = useAuthStore()
    return !!authStore.refreshToken && decodeToken(authStore.refreshToken).exp * 1000 > Date.now()
  }

  static refreshDuration(): number {
    const authStore = useAuthStore()
    return !!authStore.refreshToken && decodeToken(authStore.refreshToken).exp * 1000 || 0
  }

  static getAccessToken(): string | null {
    const authStore = useAuthStore()
    if (!this.isAccessValid()) return null
    return authStore.accessToken
  }

  static getRefreshToken(): string | null {
    const authStore = useAuthStore()
    if (!this.isRefreshValid()) return null
    return authStore.refreshToken
  }

  static isAuthenticated(): boolean {
    return this.isAccessValid() || this.isRefreshValid()
  }

  static shouldRefreshToken(): boolean {
    return !this.isAccessValid() && this.isRefreshValid()
  }

  static shouldRemember(value: boolean): void {
    const authStore = useAuthStore()
    authStore.setRemember(value)
  }

  static setOktaKey(value: string): void {
    if (this.isAuthenticated()) return
    this.clearTokens()
    const authStore = useAuthStore()
    authStore.setOktaKey(value)
  }

  static getOktaKey(): string | null {
    const authStore = useAuthStore()
    return authStore.oktaKey
  }

  static clearTokens(): void {
    const authStore = useAuthStore()
    authStore.clearTokens()
  }

  static subscribe(callback: (state: void) => void) {
    const store = useAuthStore()
    store.$subscribe(() => callback())
  }
}
